import { makeApi, URLS } from "Utils/apiURL";
import { timeAgo, mainActionOf } from "../signalMeta";

// Data for one tracked signal's page: the saved signal, everything it found, and what watching it costs.
// Plain functions on makeApi (which resolves to the axios error on failure, never throws).

const fill = (url, id) => url.replace("<<id>>", encodeURIComponent(id));
const ok = res => !!res && res.status >= 200 && res.status < 300;

/** The saved signal view ({signal, signalLabel, displayName, scopeLabel, firedLast30Days, agents, ...}) or null. */
export const fetchTracked = async id => {
  const res = await makeApi({ url: fill(URLS.signalsSavedOne, id), method: "GET" });
  return ok(res) && res.data ? res.data : null;
};

// ponytail: one page of 100; add paging when a single tracker finds more than that.
export const fetchMoves = async id => {
  const res = await makeApi({ url: URLS.signalsFeed, method: "GET", params: { view: "all", savedSignalId: id, page: 0, size: 100 } });
  return (ok(res) && res.data && res.data.items) || [];
};

/** {records, creditsPerCheck, ...} for this signal's own scope, or null when the server can't say. */
export const fetchEstimate = async view => {
  const s = view?.signal || {};
  const res = await makeApi({
    url: URLS.signalsEstimate,
    method: "POST",
    data: { signalType: s.signalType, scopeType: s.scopeType, scopeListId: s.scopeListId, conditions: s.conditions, checkEveryDays: s.checkEveryDays, savedSignalId: s.id }
  });
  return ok(res) && res.data ? res.data : null;
};

export const setPaused = async (id, paused) => {
  const res = await makeApi({ url: fill(paused ? URLS.signalsPause : URLS.signalsResume, id), method: "POST" });
  return ok(res) ? { ok: true } : { error: res?.response?.data?.error || "Could not update the signal." };
};

export const markRead = id => makeApi({ url: fill(URLS.signalsFeedRead, id), method: "POST" });

const RUN_TEXT = { QUEUED: "queued", RUNNING: "working on it", WAITING_APPROVAL: "waiting for your OK", SUCCEEDED: "done", PARTIAL: "partly done", FAILED: "failed", CANCELLED: "cancelled" };

/** One feed entry as a row of the tracker table. Fit and filter reasons are read from the payload when the server sends them. */
export const moveOf = item => {
  const e = item?.event || {};
  const p = e.payload || {};
  const [subTitle, subCompany] = String(item?.entitySubtitle || "").split(" · ");
  const runs = item?.agentRuns || [];
  const isNew = e.state === "OPEN" && !e.read && runs.length === 0;
  const fit = typeof p.fitsIcp === "boolean" ? p.fitsIcp : null;
  const filtered = p.filteredReason || (fit === false ? p.fitReason : "");
  let happened;
  if (runs.length) {
    const r = runs[runs.length - 1];
    happened = `${r.agentName || "An agent"}: ${RUN_TEXT[r.status] || String(r.status || "").toLowerCase()}`;
  } else if (e.state === "DISMISSED") happened = filtered ? `Filtered out: ${filtered}` : "Skipped";
  else if (e.state === "HANDLED") happened = "Handled";
  else if (e.state === "SNOOZED") happened = "Snoozed";
  else happened = `${e.read ? "Seen" : "New"} · ${timeAgo(e.detectedAt) || "just now"}`;
  // Account events about a person (a new leader): the person is in the payload and the
  // account is their company. Without this the row put the company under Person.
  const leader = e.entityType === "ACCOUNT" && p.name ? p.name : "";
  return {
    id: e.id,
    at: new Date(e.detectedAt).getTime() || 0,
    entityId: e.entityId,
    isAccount: e.entityType === "ACCOUNT",
    isJobChange: e.signalType === "job_change",
    name: leader || item?.entityName || "Someone",
    photo: item?.entityPhoto || p.photoUrl || "",
    title: p.newTitle || (leader ? p.title : "") || subTitle || "",
    toCompany: p.toCompany || (leader ? item?.entityName || "" : ""),
    toDomain: p.toCompanyDomain || p.toDomain || "",
    fromCompany: p.fromCompany || subCompany || "",
    fit,
    fitText: fit === true ? "Fits ICP" : fit === false ? p.fitReason || "Not a fit" : "Not scored",
    skipped: e.state === "DISMISSED" || fit === false,
    needsYou: e.state === "OPEN" && runs.length === 0,
    isNew,
    unread: e.state === "OPEN" && !e.read,
    happened,
    headline: e.headline || "",
    main: mainActionOf({ isAccount: e.entityType === "ACCOUNT", email: (item?.entity || {}).email, contactId: (item?.entity || {}).contactId }),
    email: (item?.entity || {}).email || "",
    domain: item?.entityDomain || (item?.entity || {}).domain || "",
    item
  };
};

// Two views only: what still needs you, and everything (handled and skipped ones stay greyed in place there).
// "All" is named by what this signal finds (see nounOf).
export const CHIPS = [
  ["needs", "To act on", m => m.needsYou],
  ["all", "All", () => true]
];

/** What one tracked signal finds, plural: a job change finds moves, hiring finds roles, a custom signal finds matches. */
export const nounOf = type => (type === "job_change" ? "moves" : type === "hiring_intel" ? "roles" : type === "custom" ? "matches" : "signals");

export const chipCounts = moves => CHIPS.reduce((out, [key, , test]) => ({ ...out, [key]: moves.filter(test).length }), {});
